package com.evoapps.evoshift.domain.timeline

import com.evoapps.evoshift.domain.model.Absence
import com.evoapps.evoshift.domain.model.AbsenceStatus
import com.evoapps.evoshift.domain.model.AbsenceType
import com.evoapps.evoshift.domain.model.WorkRequest
import com.evoapps.evoshift.domain.model.WorkRequestKind
import com.evoapps.evoshift.domain.requests.RequestType
import kotlinx.datetime.LocalDate

/*
 * SH-224: the person's requests on their timeline (SH-212), every SH-223 type, in three lanes so
 * it stays readable. The requests stay where SH-223 keeps them (`absences` with `type` and
 * `kind`, `work_requests` for remote work, events and overtime): the timeline only shows them.
 * They are never dragged: a request changes by its own rules (sent, approved, rejected, withdrawn),
 * so moving one would be a new request (docs/employee-timeline.md).
 */

/** The request lanes, in the order drawn (below the timeline's own lanes). */
enum class RequestLane {
    /** "Nieobecności": every absence type (leave, family and care, health, time off in lieu). */
    ABSENCES,
    /** "Praca zdalna i wydarzenia": remote work (with its place) and work events, trips, training. */
    REMOTE_AND_EVENTS,
    /** "Nadgodziny": overtime. */
    OVERTIME;

    companion object {
        /** Every absence type is an absence: leave, care, health and time off in lieu alike. */
        @Suppress("UNUSED_PARAMETER")
        fun of(type: AbsenceType): RequestLane = ABSENCES

        fun of(kind: WorkRequestKind): RequestLane = when (kind) {
            WorkRequestKind.REMOTE_WORK, WorkRequestKind.WORK_EVENT -> REMOTE_AND_EVENTS
            WorkRequestKind.OVERTIME -> OVERTIME
        }

        fun of(type: RequestType): RequestLane = when (type) {
            is RequestType.Leave -> of(type.type)
            is RequestType.Work -> of(type.kind)
        }
    }
}

/**
 * How a request is drawn: approved ones solid, pending ones outlined. Rejected ones are hidden
 * unless asked for ("pokaż odrzucone"), then drawn faded and struck. A withdrawn (cancelled)
 * request is deleted (SH-223 keeps no cancelled status), so there is nothing left to draw.
 */
enum class RequestSpanStyle {
    SOLID, OUTLINED, DECLINED;

    /** Shown without "show rejected". */
    val shownByDefault: Boolean get() = this != DECLINED

    companion object {
        fun of(status: AbsenceStatus): RequestSpanStyle = when (status) {
            AbsenceStatus.APPROVED -> SOLID
            AbsenceStatus.PENDING -> OUTLINED
            AbsenceStatus.REJECTED -> DECLINED
        }
    }
}

/** One request on the timeline: an absence ([absence]) or a work request ([work]). */
data class TimelineRequest(
    val type: RequestType,
    val status: AbsenceStatus,
    /** Both days included; an hour-based request covers one day. */
    val from: LocalDate,
    val until: LocalDate,
    /** "HH:mm", both or neither. */
    val startTime: String? = null,
    val endTime: String? = null,
    val absence: Absence? = null,
    val work: WorkRequest? = null
) {
    val id: String get() = (absence?.id ?: work?.id).orEmpty()
    val lane: RequestLane get() = RequestLane.of(type)
    val style: RequestSpanStyle get() = RequestSpanStyle.of(status)

    /** Part of one day: drawn as a narrow mark on its day. */
    val isHourly: Boolean get() = !startTime.isNullOrBlank() && !endTime.isNullOrBlank()

    /** Remote work's place, a work event's (optional). */
    val placeName: String? get() = work?.placeName
    val note: String? get() = absence?.reason ?: work?.note
    val rejectionReason: String? get() = absence?.rejectionReason ?: work?.rejectionReason

    /** The days it covers within [range] (null: none). */
    fun overlaps(range: ClosedRange<LocalDate>): Boolean = from <= range.endInclusive && until >= range.start

    /** Its tag and key: unique across both collections. */
    val key: String get() = "${if (absence != null) "absence" else "work"}:$id"

    companion object {
        fun of(absence: Absence): TimelineRequest? {
            val from = day(absence.startDate) ?: return null
            val until = day(absence.endDate)?.takeIf { it >= from } ?: from
            return TimelineRequest(
                RequestType.Leave(absence.type), absence.status, from, until,
                absence.startTime.takeIf { absence.isHourly }, absence.endTime.takeIf { absence.isHourly }, absence = absence
            )
        }

        fun of(work: WorkRequest): TimelineRequest? {
            val from = day(work.startDate) ?: return null
            val until = day(work.endDate)?.takeIf { it >= from } ?: from
            return TimelineRequest(
                RequestType.Work(work.kind), work.status, from, until,
                work.startTime.takeIf { work.isHourly }, work.endTime.takeIf { work.isHourly }, work = work
            )
        }

        private fun day(text: String): LocalDate? = runCatching { LocalDate.parse(text.take(10)) }.getOrNull()
    }
}

/** What the request lanes show: the person's requests in the visible range. */
data class TimelineRequests(
    val requests: List<TimelineRequest> = emptyList(),
    /** "Pokaż odrzucone": rejected requests drawn too. */
    val showDeclined: Boolean = false
) {
    /** The requests drawn: rejected ones only when asked for. */
    val shown: List<TimelineRequest> get() = requests.filter { showDeclined || it.style.shownByDefault }

    /** How many rejected requests the toggle would show (the toggle is offered only with some). */
    val declinedCount: Int get() = requests.count { !it.style.shownByDefault }

    /** The requests of [lane], by start (whole days before hours on the same day). */
    fun of(lane: RequestLane): List<TimelineRequest> =
        shown.filter { it.lane == lane }.sortedWith(compareBy<TimelineRequest> { it.from }.thenBy { it.startTime.orEmpty() })

    fun find(key: String): TimelineRequest? = requests.firstOrNull { it.key == key }

    /**
     * The rows [lane] is drawn in on the wide timeline: requests that share a day (remote work
     * and an event, two hour-based requests) go one under the other, never over each other.
     */
    fun rowsOf(lane: RequestLane): List<List<TimelineRequest>> {
        val rows = mutableListOf<MutableList<TimelineRequest>>()
        of(lane).forEach { request ->
            val row = rows.firstOrNull { it.last().until < request.from }
            if (row != null) row += request else rows += mutableListOf(request)
        }
        return rows
    }

    companion object {
        /** Every lane, shown even when empty (each is where a request of its kind is added). */
        val LANES: List<RequestLane> = RequestLane.entries

        /**
         * The requests of [absences] and [work] at [workplaceId] that touch [range]; documents a
         * later version wrote in a shape this one cannot read are left out by the repositories.
         */
        fun of(
            absences: List<Absence>,
            work: List<WorkRequest>,
            workplaceId: String,
            range: ClosedRange<LocalDate>,
            showDeclined: Boolean = false
        ): TimelineRequests = TimelineRequests(
            requests = (absences.filter { it.workplaceId == workplaceId }.mapNotNull { TimelineRequest.of(it) } +
                work.filter { it.workplaceId == workplaceId }.mapNotNull { TimelineRequest.of(it) })
                .filter { it.overlaps(range) }
                .distinctBy { it.key },
            showDeclined = showDeclined
        )
    }
}
